import React, { useState, useEffect, useRef } from 'react';
import { Button, Input, Select, message, Tag, Upload, Typography, Card, DatePicker, Switch, Checkbox, Radio, InputNumber, Space, Pagination, Progress } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, PlayCircleOutlined, UploadOutlined, CalendarOutlined, PauseCircleOutlined, ExperimentOutlined, FileExcelOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { useTable, useDrawer, useConfirmModal, usePermission } from '@/hooks/index';
import { GlobalTable, GlobalForm, ConfirmModal, PermissionWrapper } from '@/index';
import type { TableColumn, TableAction } from '@/types/table';
import type { FormField } from '@/types/form';
import { versionsAPI } from '@/services/api/versions';
import { filesAPI } from '@/services/api/files';
import { deviceModelsAPI } from '@/services/api/device-models';
import { devicesAPI } from '@/services/api/devices';
import type { Version, VersionStatus, CreateVersionRequest, UpdateVersionRequest, CreateStrategyRequest, UpdateStrategyRequest, DiffRelation, RolloutScheduleItem } from '@/types/api';
import type { DeviceModel } from '@/types/api';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const { Text } = Typography;

// 扩展版本类型，用于表格显示（包含机型名称）
// 列表接口返回的数据可能不包含所有字段，所以使用 Partial
interface VersionWithModelName extends Partial<Version> {
  id: number; // id 是必需的
  version: string; // version 是必需的
  status: VersionStatus; // status 是必需的
  created_at: string; // created_at 是必需的
  model_name?: string; // 后端返回的机型名称
  key?: string; // 表格需要的 key
}

/**
 * 差分包管理抽屉内容
 */
const DiffPackageManager: React.FC<{ version: VersionWithModelName }> = ({ version }) => {
  const [loading, setLoading] = useState(false);
  const [fetchingDetail, setFetchingDetail] = useState(false);
  const [diffList, setDiffList] = useState<DiffRelation[]>([]);
  const [versionDetail, setVersionDetail] = useState<VersionWithModelName | null>(null);

  // 获取版本详情（如果缺少必要字段）
  const fetchVersionDetail = async (): Promise<VersionWithModelName | null> => {
    if (!version?.id) {
      console.warn('版本ID不存在，无法获取差分关系');
      return null;
    }

    // 如果已经有 model_id 和 version，直接返回
    if (version.model_id && version.version) {
      return version as VersionWithModelName;
    }

    try {
      setFetchingDetail(true);
      console.log('版本信息不完整，开始获取版本详情，ID:', version.id);
      const detail = await versionsAPI.getVersionById(version.id);
      console.log('版本详情获取成功:', detail);
      return detail as VersionWithModelName;
    } catch (error: any) {
      console.error('获取版本详情失败:', error);
      message.error(error.message || '获取版本详情失败');
      return null;
    } finally {
      setFetchingDetail(false);
    }
  };

  // 获取差分关系列表
  const fetchDiffs = async () => {
    if (!version?.id) {
      console.warn('版本ID不存在，无法获取差分关系');
      return;
    }

    try {
      setLoading(true);

      // 先获取版本详情（如果缺少必要字段）
      let currentVersion: VersionWithModelName;

      // 如果已经有缓存的详情，使用缓存的
      if (versionDetail && versionDetail.model_id && versionDetail.version) {
        currentVersion = versionDetail;
      }
      // 如果传入的版本已经有完整信息，直接使用
      else if (version.model_id && version.version) {
        currentVersion = version as VersionWithModelName;
      }
      // 否则需要获取详情
      else {
        const detail = await fetchVersionDetail();
        if (!detail || !detail.model_id || !detail.version) {
          console.error('无法获取版本详情或版本信息不完整，终止获取差分关系');
          message.error('无法获取版本详情，请重试');
          return;
        }
        currentVersion = detail;
        setVersionDetail(detail);
      }

      // 确保类型安全
      const modelId = currentVersion.model_id;
      const toVersion = currentVersion.version;

      if (!modelId || !toVersion) {
        console.error('版本信息不完整，无法获取差分关系');
        message.error('版本信息不完整，无法获取差分关系');
        return;
      }

      console.log('开始获取差分关系，参数:', {
        model_id: modelId,
        to_version: toVersion,
      });

      const response = await versionsAPI.getVersionDiffs({
        model_id: modelId,
        to_version: toVersion,
      });

      console.log('差分关系获取成功:', response);
      setDiffList(response.relations || []);
    } catch (error: any) {
      console.error('获取差分关系列表失败:', error);
      message.error(error.message || '获取差分关系列表失败');
      setDiffList([]);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (version?.id) {
      fetchDiffs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version?.id]);

  const hasDiffs = (diffList && diffList.length > 0);
  const displayVersion = versionDetail || version;

  return (
    <div className="space-y-4">
      <Card
        size="small"
        title="当前版本信息"
        styles={{
          header: {
            background: 'var(--bg-primary)',
            borderBottom: '1px solid var(--border-primary)',
            color: 'var(--text-primary)',
          },
          body: {
            background: 'var(--bg-primary)',
          },
        }}
      >
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <span className="primary-text mr-2">机型</span>
            <span className="info-text">{displayVersion.model_name || '未知'}</span>
          </div>
          <div>
            <span className="primary-text mr-2">版本号</span>
            <span className="info-text">{displayVersion.version || '-'}</span>
          </div>
          {/* <div>
            <span className="primary-text mr-2">完整包大小</span>
            <span className="info-text">{displayVersion.package_size_mb ? `${displayVersion.package_size_mb} MB` : '-'}</span>
          </div> */}
        </div>
      </Card>

      <Card
        size="small"
        title="差分关系列表"
        loading={loading || fetchingDetail}
        styles={{
          header: {
            background: 'var(--bg-primary)',
            borderBottom: '1px solid var(--border-primary)',
            color: 'var(--text-primary)',
          },
          body: {
            background: 'var(--bg-primary)',
          },
        }}
      >
        <div className="text-xs primary-text mb-3">显示所有前序版本到当前版本的差分包，按创建顺序排列</div>
        {hasDiffs ? (
          <div className="space-y-4">
            {diffList.map((d, index) => (
              <div key={`${d.from_version}-${d.to_version}-${index}`} className="p-4 rounded-md border" style={{ borderColor: 'var(--border-primary)', background: 'var(--bg-primary)' }}>
                <div className="flex items-center justify-between">
                  <div className="text-base font-medium primary-text">
                    {`${d.from_version} → ${d.to_version}`}
                  </div>
                  <Tag color="success">已上传</Tag>
                </div>

                <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="primary-text mr-2">文件名:</span>
                    <span className="info-text">{d.file_name || '-'}</span>
                  </div>
                  <div>
                    <span className="primary-text mr-2">文件大小:</span>
                    <span className="info-text">
                      {d.file_size ? `${(d.file_size / 1024 / 1024).toFixed(2)} MB` : '-'}
                    </span>
                  </div>
                  {d.md5_hash && (
                    <div className="col-span-2">
                      <span className="primary-text mr-2">MD5:</span>
                      <span className="info-text text-xs font-mono">{d.md5_hash}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-sm text-gray-500">暂无差分关系</div>
        )}
      </Card>


    </div>
  );
};

/**
 * 版本表单组件
 */
interface VersionFormProps {
  mode: 'add' | 'edit';
  initialValues?: Partial<VersionWithModelName>;
  onSuccess?: () => void;
}

const VersionForm: React.FC<VersionFormProps> = ({ mode, initialValues, onSuccess }) => {
  const { closeDrawer } = useDrawer();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [fileList, setFileList] = useState<File[]>([]);
  const [versionData, setVersionData] = useState<Partial<VersionWithModelName> | undefined>(initialValues);
  const [forceUpdate, setForceUpdate] = useState<boolean>(initialValues?.force_update || false);
  // 默认描述：新增时给一个通用文案，编辑时使用已有描述
  const [description, setDescription] = useState<string>(initialValues?.description || '新增特性与性能优化');
  // 上传进度
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  // 机型列表暂时不使用，保留以备后用
  // const [deviceModels, setDeviceModels] = useState<DeviceModel[]>([]);

  // 编辑模式下，获取版本详情
  useEffect(() => {
    const fetchVersionDetail = async () => {
      if (mode === 'edit' && initialValues?.id) {
        try {
          setFetching(true);
          console.log('开始获取版本详情，ID:', initialValues.id);
          const detail = await versionsAPI.getVersionById(initialValues.id);
          console.log('版本详情获取成功:', detail);

          // 更新版本数据
          setVersionData(detail);
          // 更新表单字段
          setForceUpdate(detail.force_update || false);
          setDescription(detail.description || '');
        } catch (error: any) {
          console.error('获取版本详情失败:', error);
          message.error(error.message || '获取版本详情失败');
        } finally {
          setFetching(false);
        }
      }
    };

    fetchVersionDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, initialValues?.id]);

  const formFields: FormField[] = [
    // 基本信息分组
    {
      name: 'basicInfo',
      label: '基本信息',
      type: 'custom',
      customRender: () => (
        <Card
          title="基本信息"
          size="small"
          className="mb-4"
          styles={{
            header: {
              background: 'var(--bg-primary)',
              borderBottom: '1px solid var(--border-primary)',
              fontSize: '14px',
              fontWeight: 'bold',
              color: 'var(--text-primary)',
            },
            body: {
              background: 'var(--bg-primary)',
            },
          }}
        >
          <div className="space-y-4">
            {mode === 'add' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 primary-text">
                  上传包文件 <span className="text-red-500">*</span>
                </label>
                {fileList.length > 0 && fileList[0] && (
                  <div className="mb-2 p-2 bg-green-50 border border-green-200 rounded text-sm">
                    <span className="text-green-700">已选择文件: {fileList[0].name} ({(fileList[0].size / 1024 / 1024).toFixed(2)} MB)</span>
                  </div>
                )}
                {isUploading && (
                  <div className="mb-2 p-3 bg-blue-50 border border-blue-200 rounded">
                    <div className="text-sm text-blue-700 mb-2">正在上传文件...</div>
                    <Progress
                      percent={uploadProgress}
                      status={uploadProgress === 100 ? 'success' : 'active'}
                      strokeColor={{
                        '0%': '#108ee9',
                        '100%': '#87d068',
                      }}
                    />
                  </div>
                )}
                <Upload.Dragger
                  name="file"
                  multiple={false}
                  maxCount={1}
                  beforeUpload={() => false}
                  disabled={isUploading}
                  onChange={(info) => {
                    console.log('Upload onChange 触发:', info);
                    console.log('fileList:', info.fileList);
                    console.log('file:', info.file);

                    // 当 beforeUpload 返回 false 时，文件状态可能是 undefined
                    // 只支持上传一个文件，如果选择了新文件，替换旧文件
                    const fileStatus = info.file.status;

                    if (fileStatus !== 'removed' && fileStatus !== 'error') {
                      // 优先使用 originFileObj，如果没有则从 fileList 获取
                      let file: File | undefined = undefined;

                      if (info.file.originFileObj) {
                        file = info.file.originFileObj as File;
                        console.log('从 originFileObj 获取文件:', file.name);
                      } else if (info.fileList.length > 0) {
                        // 尝试从 fileList 获取（只取最后一个文件，确保只有一个）
                        const lastFile = info.fileList[info.fileList.length - 1];
                        if (lastFile.originFileObj) {
                          file = lastFile.originFileObj as File;
                          console.log('从 fileList 最后一个文件获取:', file.name);
                        }
                      }

                      if (file) {
                        console.log('设置文件到 fileList:', file.name, file.size);
                        // 确保只保留一个文件
                        setFileList([file]);
                      } else {
                        console.warn('无法获取文件对象');
                        // 如果无法获取文件，清空 fileList
                        setFileList([]);
                      }
                    } else {
                      console.log('文件被移除或出错，清空 fileList');
                      setFileList([]);
                    }
                  }}
                  onRemove={() => {
                    console.log('文件被移除');
                    setFileList([]);
                    return true;
                  }}
                  className=" hover:border-blue-400"
                >
                  <p className="ant-upload-drag-icon text-2xl">
                    <UploadOutlined style={{ color: 'var(--color-primary)' }} />
                  </p>
                  <p className="ant-upload-text text-gray-600 primary-text">拖拽文件到这里或点击选择文件</p>
                  <p className="ant-upload-hint text-gray-400 primary-text">仅支持上传一个文件，支持 ZIP, IMG, APK 格式, 最大 2GB</p>
                </Upload.Dragger>
              </div>
            )}

            {mode === 'edit' && versionData && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 primary-text">创建时间</label>
                  <div className="flex items-center px-3 py-2 bg-white border border-gray-200 rounded-md" style={{ background: 'var(--bg-primary)', borderColor: 'var(--border-primary)' }}>
                    <CalendarOutlined className="mr-2" style={{ color: 'var(--text-tertiary)' }} />
                    <Text style={{ color: 'var(--text-primary)' }}>
                      {versionData.created_at ? dayjs(versionData.created_at).format('YYYY-MM-DD HH:mm:ss') : '-'}
                    </Text>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 primary-text">当前状态</label>
                  <div className="px-3 py-2">
                    <Tag color={
                      versionData.status === 'publishing' ? 'success' :
                        versionData.status === 'paused' ? 'warning' :
                          versionData.status === 'testing' ? 'processing' :
                            versionData.status === 'offline' ? 'default' : 'default'
                    }>
                      {versionData.status === 'publishing' ? '发布中' :
                        versionData.status === 'paused' ? '已暂停' :
                          versionData.status === 'testing' ? '测试中' :
                            versionData.status === 'offline' ? '已下线' : '草稿'}
                    </Tag>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Card>
      ),
    },
    // 发布配置分组
    {
      name: 'releaseConfig',
      label: '发布配置',
      type: 'custom',
      customRender: () => (
        <Card
          title="发布配置"
          size="small"
          styles={{
            header: {
              background: 'var(--bg-primary)',
              borderBottom: '1px solid var(--border-primary)',
              fontSize: '14px',
              fontWeight: 'bold',
              color: 'var(--text-primary)',
            },
            body: {
              background: 'var(--bg-primary)',
            },
          }}
        >
          <div className="space-y-4">
            <div>
              <label className="flex items-center">
                <span className="text-sm font-medium text-gray-700 primary-text mr-2">强制更新</span>
                <Checkbox
                  checked={forceUpdate}
                  onChange={(e) => setForceUpdate(e.target.checked)}
                />
              </label>
              <Text type="secondary" className="text-xs block mt-1 primary-text">设备必须安装此更新才能继续使用</Text>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 primary-text">更新说明</label>
              <Input.TextArea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full"
                style={{
                  background: 'var(--bg-primary)',
                  borderColor: 'var(--border-primary)',
                }}
                rows={4}
                placeholder="描述本次更新的内容,如新增功能、修复问题等..."
              />
            </div>
          </div>
        </Card>
      ),
    },
  ];

  const handleFinish = async (_values: any) => {
    try {
      setLoading(true);
      console.log('handleFinish 被调用');
      console.log('当前 fileList:', fileList);
      console.log('fileList.length:', fileList.length);

      if (mode === 'add') {
        // 创建版本：先上传文件
        console.log('模式: add');
        if (fileList.length === 0 || !fileList[0]) {
          console.error('文件列表为空或文件无效');
          message.error('请先上传包文件');
          setLoading(false);
          return;
        }

        const file = fileList[0];
        console.log('准备上传的文件:', file);
        console.log('文件名称:', file.name);
        console.log('文件大小:', file.size);
        console.log('文件类型:', file.type);

        if (!file) {
          console.error('文件对象无效');
          message.error('文件无效');
          setLoading(false);
          return;
        }

        try {
          // 上传文件
          setIsUploading(true);
          setUploadProgress(0);
          console.log('开始上传文件:', file.name);

          const uploadResponse = await filesAPI.uploadFile(file, (progress) => {
            setUploadProgress(progress);
          });
          setIsUploading(false);

          console.log('文件上传成功，文件ID:', uploadResponse.id);

          // 创建版本（后端会从文件中解析版本信息）
          message.loading('正在创建版本...', 0);
          const createData: CreateVersionRequest = {
            file_id: uploadResponse.id,
            description: description || undefined,
            force_update: forceUpdate,
          };

          console.log('创建版本数据:', createData);
          await versionsAPI.createVersion(createData);
          message.destroy();
          message.success('版本创建成功！');
        } catch (uploadError: any) {
          setIsUploading(false);
          setUploadProgress(0);
          message.destroy(); // 清除 loading 提示
          console.error('上传或创建版本失败:', uploadError);
          throw uploadError; // 重新抛出，让外层 catch 处理
        }
      } else {
        // 更新版本
        if (!versionData?.id) {
          message.error('版本ID不存在');
          setLoading(false);
          return;
        }

        // 更新版本（只能更新描述信息）
        const updateData: UpdateVersionRequest = {
          description: description || undefined,
          force_update: forceUpdate,
        };

        console.log('更新版本数据:', updateData);
        console.log('版本ID:', versionData.id);
        await versionsAPI.updateVersion(versionData.id, updateData);
        message.success('版本更新成功！');
      }

      closeDrawer();
      onSuccess?.();
    } catch (error: any) {
      console.error('操作失败:', error);
      message.error(error.message || '操作失败，请重试！');
    } finally {
      setLoading(false);
    }
  };

  // 如果正在获取详情，显示加载状态
  if (mode === 'edit' && fetching) {
    return (
      <div className="p-6 text-center">
        <div className="text-gray-500">正在加载版本详情...</div>
      </div>
    );
  }

  return (
    <GlobalForm
      fields={formFields}
      initialValues={versionData || initialValues}
      onFinish={handleFinish}
      loading={loading}
      submitText={mode === 'add' ? '创建版本' : '保存修改'}
    />
  );
};

/**
 * 配置测试设备组件
 */
interface TestDeviceFormProps {
  version: VersionWithModelName;
  onSuccess?: () => void;
}

const TestDeviceForm: React.FC<TestDeviceFormProps> = ({ version, onSuccess }) => {
  const { closeDrawer } = useDrawer();
  const [loading, setLoading] = useState(false);
  const [deviceList, setDeviceList] = useState<any[]>([]);
  const [selectedSNs, setSelectedSNs] = useState<string[]>([]);
  const [selectedDevices, setSelectedDevices] = useState<any[]>([]); // 已选择设备的详细信息（多选，按 SN 去重）
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [fetchingDevices, setFetchingDevices] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalDevices, setTotalDevices] = useState(0);

  // 获取设备列表（带分页）
  useEffect(() => {
    const fetchDevices = async () => {
      try {
        setFetchingDevices(true);
        const response = await devicesAPI.getDevices({
          page: currentPage,
          size: pageSize,
          search: searchKeyword,
          ...(version.model_id && { model_id: version.model_id }),
        });
        setDeviceList(response.list || []);
        setTotalDevices(response.total || 0);
      } catch (error: any) {
        console.error('获取设备列表失败:', error);
        message.error(error.message || '获取设备列表失败');
      } finally {
        setFetchingDevices(false);
      }
    };

    fetchDevices();
  }, [version.model_id, searchKeyword, currentPage, pageSize]);

  // 搜索时重置到第一页
  useEffect(() => {
    setCurrentPage(1);
  }, [searchKeyword]);

  // 打开时获取已配置的测试设备列表
  useEffect(() => {
    const fetchTestDevices = async () => {
      if (!version.id) return;
      try {
        const list = await versionsAPI.getTestDevices(version.id);
        if (!Array.isArray(list)) return;
        // 转成前端展示需要的结构（以 SN 为主）
        const devices = list.map((item) => ({
          sn: item.device_sn,
          model_name: item.model_name ?? '未知',
          region: item.region ?? '-',
          local_version: item.current_version ?? '-',
        }));
        setSelectedDevices(devices);
        setSelectedSNs(devices.map((d) => d.sn));
      } catch (error) {
        // 已配置列表获取失败不影响使用，打印日志即可
        console.log('获取已有测试设备列表失败:', error);
      }
    };

    fetchTestDevices();
  }, [version.id]);

  const handleSave = async () => {
    if (!version.id) {
      message.error('版本ID不存在');
      return;
    }

    // 必须至少选择一个设备
    if (selectedSNs.length === 0) {
      message.warning('请至少选择一个测试设备');
      return;
    }

    // 直接使用 SN 数组
    const deviceSNs = Array.from(new Set(selectedSNs)).filter((sn) => !!sn);

    try {
      setLoading(true);

      // 将所有选中的设备 SN 作为数组传给后端，后端会覆盖式设置测试设备列表
      await versionsAPI.setTestDevices(version.id, deviceSNs);
      message.success(
        version.status === 'draft'
          ? '测试设备配置成功，版本将进入测试状态！'
          : '测试设备配置成功！'
      );

      closeDrawer();
      onSuccess?.();
    } catch (error: any) {
      console.error('配置测试设备失败:', error);
      message.error(error.message || '配置测试设备失败，请重试！');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="p-4 rounded-lg border" style={{ background: 'var(--bg-primary)', borderColor: 'var(--border-primary)' }}>
        <div className="text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>当前版本</div>
        <div className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>
          {version.model_name || '未知'} - {version.version}
        </div>
        <div className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
          当前状态: {version.status === 'draft' ? '草稿' : version.status === 'testing' ? '测试中' : '其他'}
        </div>
      </div>

      {/* 如果是草稿状态，显示提示信息 */}
      {version.status === 'draft' && (
        <div className="p-3 rounded-md border" style={{
          background: 'var(--color-primary-light)',
          borderColor: 'var(--color-primary)',
        }}>
          <div className="flex items-start">
            <ExperimentOutlined style={{ color: 'var(--color-primary)', marginRight: 8, marginTop: 2 }} />
            <div>
              <div className="text-sm font-medium" style={{ color: 'var(--color-primary)', marginBottom: 4 }}>
                进入测试状态
              </div>
              <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                配置测试设备后，版本将自动进入测试状态。只有选择的测试设备会收到更新推送。
              </div>
            </div>
          </div>
        </div>
      )}

      <Card
        size="small"
        styles={{
          header: {
            fontWeight: 600,
            color: 'var(--text-primary)',
            background: 'var(--bg-primary)',
            borderBottom: '1px solid var(--border-primary)',
          },
          body: {
            background: 'var(--bg-primary)',
            padding: '16px',
          },
        }}
        title="选择测试设备"
      >
        {/* 搜索框放在 Card 内部，在设备列表上方 */}
        <div className="mb-4" style={{ padding: 0 }}>
          <Input
            placeholder="搜索设备（设备ID、机型、地区）"
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            allowClear
            style={{ width: '100%' }}
            className="test-device-search-input"
          />
        </div>

        {/* 已选择的设备（多选，仅展示） */}
        {selectedDevices.length > 0 && (
          <div className="mb-4 pb-4 border-b" style={{ borderColor: 'var(--border-primary)' }}>
            <div className="text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
              已选择的测试设备（{selectedDevices.length}）
            </div>
            <div className="max-h-32 overflow-y-auto">
              <Space direction="vertical" style={{ width: '100%' }} size="small">
                {selectedDevices.map((device, index) => (
                  <div
                    key={`${device.sn}-${index}`}
                    className="flex items-center justify-between p-2 rounded"
                    style={{
                      background: 'var(--bg-secondary)',
                      border: '1px solid var(--border-primary)',
                    }}
                  >
                    <div className="flex-1">
                      <div className="text-sm" style={{ color: 'var(--text-primary)' }}>
                        {device.sn}
                      </div>
                      <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                        {device.model_name || '未知'} | {device.region || '-'} | {device.local_version || '-'}
                      </div>
                    </div>
                  </div>
                ))}
              </Space>
            </div>
          </div>
        )}

        {/* 可选择的设备列表 */}
        <div className="mb-2">
          <div className="text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
            选择设备
          </div>
          <div className="max-h-96 overflow-y-auto">
            {fetchingDevices ? (
              <div className="text-center py-8" style={{ color: 'var(--text-secondary)' }}>
                加载设备列表中...
              </div>
            ) : (
              <Checkbox.Group
                value={selectedSNs}
                onChange={(values) => {
                  const newSNs = values as string[];
                  const addedSNs = newSNs.filter((sn) => !selectedSNs.includes(sn));
                  const removedSNs = selectedSNs.filter((sn) => !newSNs.includes(sn));

                  setSelectedSNs(newSNs);

                  // 更新已选择设备列表：移除取消选中的，再加入当前页新选中的
                  setSelectedDevices((prev) => {
                    // 先移除被取消选择的 SN
                    let updated = prev.filter((d) => !removedSNs.includes(d.sn));
                    // 再把当前页新选中的设备合并进来
                    const newlySelected = deviceList
                      .filter((d) => addedSNs.includes(d.sn))
                      .map((d) => ({
                        sn: d.sn,
                        model_name: d.model_name,
                        region: d.region,
                        local_version: d.local_version,
                      }));
                    const all = [...updated, ...newlySelected];
                    // 按 SN 去重
                    const seen = new Set<string>();
                    return all.filter((d) => {
                      if (seen.has(d.sn)) return false;
                      seen.add(d.sn);
                      return true;
                    });
                  });
                }}
                style={{ width: '100%' }}
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  {deviceList.map((device) => (
                    <Checkbox key={device.id} value={device.sn} className="primary-text">
                      <div className="flex items-center justify-between w-full">
                        <span>{device.sn}</span>
                        <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                          {device.model_name} | {device.region || '-'} | {device.local_version || '-'}
                        </span>
                      </div>
                    </Checkbox>
                  ))}
                </Space>
              </Checkbox.Group>
            )}
            {deviceList.length === 0 && !fetchingDevices && (
              <div className="text-center py-8" style={{ color: 'var(--text-secondary)' }}>
                暂无设备数据
              </div>
            )}
          </div>
        </div>

        {/* 分页 */}
        {totalDevices > 0 && (
          <div className="mt-4 flex justify-between items-center">
            <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              已选择 {selectedSNs.length} 个设备
              {version.status === 'draft' && selectedSNs.length === 0 && (
                <span className="ml-2" style={{ color: 'var(--color-error)' }}>（至少选择一个设备）</span>
              )}
            </div>
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={totalDevices}
              showSizeChanger
              showQuickJumper
              showTotal={(total) => `共 ${total} 个设备`}
              pageSizeOptions={['10', '20', '50', '100']}
              onChange={(page, size) => {
                setCurrentPage(page);
                setPageSize(size);
              }}
              onShowSizeChange={(_current, size) => {
                setCurrentPage(1);
                setPageSize(size);
              }}
              size="small"
            />
          </div>
        )}

        {totalDevices === 0 && !fetchingDevices && (
          <div className="mt-4 text-sm" style={{ color: 'var(--text-secondary)' }}>
            已选择 {selectedSNs.length} 个设备
            {version.status === 'draft' && selectedSNs.length === 0 && (
              <span className="ml-2" style={{ color: 'var(--color-error)' }}>（至少选择一个设备）</span>
            )}
          </div>
        )}
      </Card>

      <div className="text-right">
        <Button
          type="primary"
          loading={loading}
          onClick={handleSave}
          disabled={selectedSNs.length === 0}
        >
          {version.status === 'draft' ? '保存并进入测试' : '保存配置'}
        </Button>
      </div>
    </div>
  );
};

/**
 * 配置策略组件
 */
interface StrategyFormProps {
  version: VersionWithModelName;
  onSuccess?: () => void;
}

const StrategyForm: React.FC<StrategyFormProps> = ({ version, onSuccess }) => {
  const { closeDrawer } = useDrawer();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasStrategy, setHasStrategy] = useState<boolean>(false);
  const [strategyId, setStrategyId] = useState<number | null>(null);

  // 时间控制
  const [startTime, setStartTime] = useState<any>();
  const [endTime, setEndTime] = useState<any>();
  const [releaseImmediately, setReleaseImmediately] = useState<boolean>(false);

  // 设备筛选
  const [regions, setRegions] = useState<string[]>([]);
  const toggleRegion = (key: string, checked: boolean) => {
    setRegions((prev) => {
      if (checked) return Array.from(new Set([...prev, key]));
      return prev.filter((k) => k !== key);
    });
  };

  // 灰度策略
  const [rolloutEnabled, setRolloutEnabled] = useState<boolean>(false);
  const [rolloutSchedule, setRolloutSchedule] = useState<RolloutScheduleItem[]>([]);
  const [progressMode, setProgressMode] = useState<'manual' | 'auto'>('manual');

  // MAC地址限制
  const [macFilterEnabled, setMacFilterEnabled] = useState<boolean>(false);
  const [targetMACs, setTargetMACs] = useState<string[]>([]);
  const [macInputValue, setMacInputValue] = useState<string>('');

  // 获取现有策略
  useEffect(() => {
    const fetchStrategy = async () => {
      if (!version.id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        console.log('开始获取策略，版本ID:', version.id);
        const strategy = await versionsAPI.getStrategy(version.id);
        console.log('策略获取成功:', strategy);

        if (strategy) {
          setHasStrategy(true);
          setStrategyId(strategy.id);
          setStartTime(strategy.start_time ? dayjs(strategy.start_time) : undefined);
          setEndTime(strategy.end_time ? dayjs(strategy.end_time) : undefined);
          setReleaseImmediately(strategy.immediate_release || false);
          setRegions(strategy.target_regions || []);
          // 灰度相关字段
          setRolloutEnabled(strategy.rollout_enabled || false);
          // MAC地址限制
          setMacFilterEnabled(strategy.mac_filter_enabled || false);
          setTargetMACs(strategy.target_macs || []);
          // 如果有时间表，使用时间表；否则根据百分比创建单个阶段
          if (strategy.rollout_schedule && strategy.rollout_schedule.length > 0) {
            setRolloutSchedule(strategy.rollout_schedule);
            // 根据是否有时间字段判断是自动还是手动模式
            const hasTime = strategy.rollout_schedule.some(item => item.time);
            setProgressMode(hasTime ? 'auto' : 'manual');
          } else {
            // 如果没有时间表，根据百分比创建手动模式的阶段
            const percentage = strategy.rollout_percentage || 100;
            setRolloutSchedule([{ time: '', percentage }]);
            setProgressMode('manual');
          }
        } else {
          // 策略不存在，使用默认值
          setHasStrategy(false);
          setStrategyId(null);
          // 初始化手动模式的默认阶段
          setRolloutSchedule([]);
          // 重置MAC地址限制
          setMacFilterEnabled(false);
          setTargetMACs([]);
        }
      } catch (error: any) {
        // 如果策略不存在（404），表示还没有配置策略
        // 检查 HTTP 状态码或业务错误码
        const statusCode = error.code || error.status || (error.details && error.details.code);
        const isNotFound = statusCode === 404;

        if (isNotFound) {
          // 404 是正常情况（策略不存在），使用默认值，表单可以正常显示和编辑
          console.log('策略不存在，可以创建新策略，表单已准备好');
          setHasStrategy(false);
          setStrategyId(null);
          // 表单字段保持默认值（空），用户可以填写
        } else {
          // 其他错误才显示错误消息
          console.error('获取策略失败:', error);
          message.error(error.message || '获取策略失败');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchStrategy();
  }, [version.id]);

  // 解析MAC地址（去除冒号和破折号，转换为大写）
  const normalizeMac = (mac: string): string => {
    return mac.replace(/[:\-]/g, '').toUpperCase().trim();
  };

  // 添加MAC地址（不验证格式）
  const addMAC = (mac: string) => {
    const normalized = normalizeMac(mac);
    if (!normalized) {
      return false; // 空值不添加
    }
    if (targetMACs.includes(normalized)) {
      return false; // 已存在，不重复添加
    }
    setTargetMACs(prev => [...prev, normalized]);
    return true;
  };

  // 从输入框添加MAC
  const handleAddMacFromInput = () => {
    if (!macInputValue.trim()) return;
    // 支持逗号、换行、空格分隔的多个MAC
    const macs = macInputValue.split(/[,\n\s]+/).filter(m => m.trim());
    let addedCount = 0;
    macs.forEach(mac => {
      if (addMAC(mac)) addedCount++;
    });
    if (addedCount > 0) {
      message.success(`成功添加 ${addedCount} 个MAC地址`);
    }
    setMacInputValue('');
  };

  // 处理Excel文件上传
  const handleExcelUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json<{ mac?: string; MAC?: string }>(worksheet, { header: ['mac'] });

        let addedCount = 0;
        jsonData.forEach((row, index) => {
          // 跳过标题行
          if (index === 0 && (row.mac === 'mac' || row.mac === 'MAC')) return;

          const mac = row.mac || row.MAC || '';
          if (mac && addMAC(mac)) {
            addedCount++;
          }
        });

        if (addedCount > 0) {
          message.success(`从Excel导入 ${addedCount} 个MAC地址`);
        }
      } catch (error) {
        message.error('解析Excel文件失败');
        console.error('Excel parse error:', error);
      }
    };
    reader.readAsBinaryString(file);
    return false; // 阻止默认上传行为
  };

  const saveStrategy = async () => {
    if (!version.id) {
      message.error('版本ID不存在');
      return;
    }

    // 验证灰度发布配置
    if (rolloutEnabled) {
      // 必须选择模式（progressMode 应该已经有默认值，但检查一下）
      if (!progressMode) {
        message.error('请选择灰度发布推进模式（手动或自动）');
        return;
      }

      // 必须配置阶段
      if (!rolloutSchedule || rolloutSchedule.length === 0) {
        message.error('启用灰度发布后，必须配置至少一个灰度阶段');
        return;
      }

      // 必须配置到100%
      const lastStage = rolloutSchedule[rolloutSchedule.length - 1];
      if (!lastStage || lastStage.percentage < 100) {
        message.error('灰度发布必须配置到100%，请添加最后一个阶段并设置为100%');
        return;
      }

      // 自动模式需要检查时间是否填写
      if (progressMode === 'auto') {
        const hasEmptyTime = rolloutSchedule.some(item => !item.time || item.time.trim() === '');
        if (hasEmptyTime) {
          message.error('自动模式下，所有时间点必须填写时间');
          return;
        }
      }
    }

    try {
      setSaving(true);

      // 构建灰度时间表
      const buildRolloutSchedule = (): RolloutScheduleItem[] | undefined => {
        if (!rolloutEnabled || rolloutSchedule.length === 0) {
          return undefined;
        }
        return rolloutSchedule.map(item => {
          // 自动模式需要将时间转换为 UTC ISO 8601 格式（"2006-01-02T15:04:05Z"）
          let timeValue = '';
          if (progressMode === 'auto' && item.time) {
            // 将本地时间转换为 UTC ISO 8601 格式
            timeValue = dayjs(item.time).utc().toISOString();
          }
          return {
            time: timeValue,
            percentage: item.percentage,
            ...(item.description && { description: item.description }),
          };
        });
      };

      // 根据是否有策略来决定创建或更新
      if (hasStrategy && strategyId) {
        // 策略已存在，更新策略
        // UpdateStrategyRequest: 所有字段都是可选的，使用指针类型
        const updateData: UpdateStrategyRequest = {
          // 时间字段：有值则转换为UTC，无值则发送清空标志
          start_time: startTime ? dayjs(startTime).utc().toISOString() : undefined,
          end_time: endTime ? dayjs(endTime).utc().toISOString() : undefined,
          clear_start_time: !startTime, // 无开始时间时清空
          clear_end_time: !endTime,     // 无结束时间时清空
          // 始终发送实际值，确保 false 也能被后端接收
          immediate_release: releaseImmediately,
          target_regions: regions.length > 0 ? regions : [],
          // 灰度相关字段 - 始终发送实际值
          rollout_enabled: rolloutEnabled,
          rollout_mode: rolloutEnabled ? progressMode : undefined,
          rollout_schedule: buildRolloutSchedule(),
          // MAC地址限制 - 必须明确传递，空数组表示清空
          mac_filter_enabled: macFilterEnabled,
          target_macs: macFilterEnabled ? targetMACs : [],
        };

        console.log('更新策略，版本ID:', version.id);
        console.log('更新策略数据:', updateData);
        console.log('targetMACs 状态:', targetMACs, '长度:', targetMACs.length);
        console.log('target_macs 字段:', updateData.target_macs);
        await versionsAPI.updateStrategy(version.id, updateData);
        message.success('策略更新成功！');
      } else {
        // 策略不存在，创建新策略
        // CreateStrategyRequest: ImmediateRelease 是必需的 bool 类型（非可选）
        const createData: CreateStrategyRequest = {
          version_id: version.id,
          // 将本地时间转换为 UTC ISO 8601 格式（"2006-01-02T15:04:05Z"）
          start_time: startTime ? dayjs(startTime).utc().toISOString() : undefined,
          end_time: endTime ? dayjs(endTime).utc().toISOString() : undefined,
          immediate_release: releaseImmediately, // 创建时是必需的 bool 类型，不能是 undefined
          target_regions: regions.length > 0 ? regions : undefined,
          // 灰度相关字段（可选）
          rollout_enabled: rolloutEnabled ? true : undefined,
          rollout_mode: rolloutEnabled ? progressMode : undefined, // 灰度推进模式
          rollout_schedule: buildRolloutSchedule(),
          // MAC地址限制
          mac_filter_enabled: macFilterEnabled,
          target_macs: macFilterEnabled ? targetMACs : [],
        };

        console.log('创建策略，版本ID:', version.id);
        console.log('创建策略数据:', createData);
        await versionsAPI.createStrategy(createData);
        message.success('策略配置成功！');
      }

      closeDrawer();
      onSuccess?.();
    } catch (e: any) {
      console.error('配置策略失败:', e);
      message.error(e.message || '配置失败，请重试！');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-center" style={{ color: 'var(--text-primary)' }}>加载中...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="p-4 rounded-lg border" style={{ background: 'var(--bg-primary)', borderColor: 'var(--border-primary)' }}>
        <div className="text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>当前版本</div>
        <div className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>
          {version.model_name || '未知'} - {version.version}
        </div>
      </div>

      {/* 时间控制 */}
      <Card
        size="small"
        styles={{
          header: {
            fontWeight: 600,
            color: 'var(--text-primary)',
            background: 'var(--bg-primary)',
            borderBottom: '1px solid var(--border-primary)',
          },
          body: {
            background: 'var(--bg-primary)',
          },
        }}
        title="时间控制"
      >
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-sm mb-2 primary-text">开始时间</div>
            <DatePicker
              showTime
              style={{ width: '100%' }}
              placeholder="选择开始时间"
              value={startTime}
              onChange={setStartTime as any}
            />
          </div>
          <div>
            <div className="text-sm mb-2 primary-text">结束时间（可选）</div>
            <DatePicker
              showTime
              style={{ width: '100%' }}
              placeholder="选择结束时间"
              value={endTime}
              onChange={setEndTime as any}
            />
          </div>
        </div>
        <div className="mt-4 rounded-md p-2" style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-primary)' }}>
          <Switch checked={releaseImmediately} onChange={setReleaseImmediately} />
          <span className="ml-2" style={{ color: 'var(--text-primary)' }}>立即发布（忽略开始时间）</span>
        </div>
      </Card>

      {/* 设备筛选条件 */}
      <Card
        size="small"
        styles={{
          header: {
            fontWeight: 600,
            color: 'var(--text-primary)',
            background: 'var(--bg-primary)',
            borderBottom: '1px solid var(--border-primary)',
          },
          body: {
            background: 'var(--bg-primary)',
          },
        }}
        title="设备筛选条件"
      >
        <div className="text-xs mb-3 primary-text" style={{ color: 'var(--text-secondary)' }}>配置设备推送的筛选条件，未选择时默认对所有设备生效</div>
        <div className="grid grid-cols-2 gap-6">
          <div>
            <div className="text-sm mb-2 primary-text">目标地区</div>
            <div className="space-y-2">
              {[
                { key: 'beijing', label: '北京' },
                { key: 'guangzhou', label: '广州' },
                { key: 'hangzhou', label: '杭州' },
              ].map(({ key, label }) => (
                <Checkbox
                  key={key}
                  checked={regions.includes(key)}
                  onChange={(e) => toggleRegion(key, e.target.checked)}
                  className="primary-text"
                >
                  {label}
                </Checkbox>
              ))}
            </div>
          </div>
          <div>
            <div className="text-sm mb-2 opacity-0">placeholder</div>
            <div className="space-y-2">
              {[
                { key: 'shanghai', label: '上海' },
                { key: 'shenzhen', label: '深圳' },
              ].map(({ key, label }) => (
                <Checkbox
                  key={key}
                  checked={regions.includes(key)}
                  onChange={(e) => toggleRegion(key, e.target.checked)}
                  className="primary-text"
                >
                  {label}
                </Checkbox>
              ))}
            </div>
          </div>
        </div>
      </Card>

      {/* MAC地址限制 */}
      <Card
        size="small"
        styles={{
          header: {
            fontWeight: 600,
            color: 'var(--text-primary)',
            background: 'var(--bg-primary)',
            borderBottom: '1px solid var(--border-primary)',
          },
          body: {
            background: 'var(--bg-primary)',
          },
        }}
        title="MAC地址限制"
      >
        <div className="text-xs mb-3 primary-text" style={{ color: 'var(--text-secondary)' }}>启用后，仅白名单内的MAC地址设备可以接收此版本更新</div>
        <div className="flex items-center justify-between p-3 rounded-md" style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-primary)' }}>
          <span style={{ color: 'var(--text-primary)' }}>启用MAC地址过滤（仅限定MAC地址设备可更新）</span>
          <Switch checked={macFilterEnabled} onChange={setMacFilterEnabled} />
        </div>

        {macFilterEnabled && (
          <div className="mt-4 space-y-4">
            {/* MAC地址输入区域 */}
            <div>
              <div className="text-sm mb-2 primary-text">添加MAC地址</div>
              <div className="flex gap-2 items-stretch">
                <div className="flex flex-col gap-2" style={{ flex: 1 }}>
                  <Input.TextArea
                    placeholder="输入MAC地址，支持多个（用逗号、换行或空格分隔）"
                    value={macInputValue}
                    onChange={(e) => setMacInputValue(e.target.value)}
                    rows={3}
                  />
                  <Button type="primary" onClick={handleAddMacFromInput}>
                    添加
                  </Button>
                </div>
                <Upload.Dragger
                  accept=".xlsx,.xls"
                  showUploadList={false}
                  beforeUpload={handleExcelUpload}
                  style={{ padding: '8px 16px', minWidth: 180 }}
                >
                  <p className="ant-upload-drag-icon" style={{ marginBottom: 4 }}>
                    <FileExcelOutlined style={{ fontSize: 24, color: 'var(--color-primary)' }} />
                  </p>
                  <p className="text-sm primary-text" style={{ marginBottom: 4 }}>拖拽Excel文件到此处</p>
                  <p className="text-xs" style={{ color: 'var(--text-secondary)', margin: 0 }}>或点击选择文件</p>
                </Upload.Dragger>
              </div>
              <div className="text-xs mt-2" style={{ color: 'var(--text-secondary)' }}>
                Excel格式：第一列为MAC地址
              </div>
            </div>

            {/* MAC地址列表 */}
            {targetMACs.length > 0 && (
              <div>
                <div className="flex justify-between items-center mb-2">
                  <div className="text-sm primary-text">已添加的MAC地址 ({targetMACs.length}个)</div>
                  <Button
                    type="link"
                    danger
                    size="small"
                    onClick={() => setTargetMACs([])}
                  >
                    清空全部
                  </Button>
                </div>
                <div className="p-3 rounded-md max-h-40 overflow-y-auto" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)' }}>
                  <div className="flex flex-wrap gap-2">
                    {targetMACs.map((mac, index) => (
                      <Tag
                        key={index}
                        closable
                        onClose={() => setTargetMACs(prev => prev.filter((_, i) => i !== index))}
                        style={{ margin: 0 }}
                      >
                        {mac}
                      </Tag>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* 灰度发布策略 */}
      <Card
        size="small"
        styles={{
          header: {
            fontWeight: 600,
            color: 'var(--text-primary)',
            background: 'var(--bg-primary)',
            borderBottom: '1px solid var(--border-primary)',
          },
          body: {
            background: 'var(--bg-primary)',
          },
        }}
        title="灰度发布策略"
      >
        <div className="text-sm mb-3 primary-text" style={{ color: 'var(--text-secondary)' }}>逐步推进发布范围，降低大规模故障风险</div>
        <div className="flex items-center justify-between p-3 rounded-md" style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-primary)' }}>
          <span style={{ color: 'var(--text-primary)' }}>启用灰度发布（分阶段逐步扩大发布范围）</span>
          <Switch checked={rolloutEnabled} onChange={setRolloutEnabled} />
        </div>

        {rolloutEnabled && (
          <div className="mt-4 space-y-4">
            <div>
              <div className="text-sm mb-2 primary-text">推进模式</div>
              <Radio.Group value={progressMode} onChange={(e) => setProgressMode(e.target.value)}>
                <Space direction="vertical">
                  <Radio value="manual" className="primary-text">手动推进 - 管理员控制每个阶段的切换</Radio>
                  <Radio value="auto" className="primary-text">自动推进 - 按设定时间自动进入下一阶段</Radio>
                </Space>
              </Radio.Group>
            </div>

            {progressMode === 'auto' && (
              <div>
                <div className="text-sm mb-3 primary-text">灰度时间表配置</div>
                <div className="text-xs mb-2 primary-text" style={{ color: 'var(--text-secondary)' }}>配置多个时间点，系统将按时间自动推进到对应百分比</div>
                {rolloutSchedule.map((item, idx) => (
                  <div key={idx} className="flex gap-2 p-3 mb-2 rounded-md border" style={{ borderColor: 'var(--border-primary)' }}>
                    <div className="flex-1">
                      <div className="text-xs mb-1 primary-text">时间点 {idx + 1}</div>
                      <DatePicker
                        showTime
                        style={{ width: '100%' }}
                        value={item.time ? dayjs(item.time) : undefined}
                        onChange={(date) => {
                          const newSchedule = [...rolloutSchedule];
                          newSchedule[idx] = {
                            ...item,
                            // 保存为本地时间格式（用于显示），在提交时会转换为 UTC ISO 8601 格式
                            time: date ? date.format('YYYY-MM-DD HH:mm:ss') : '',
                          };
                          setRolloutSchedule(newSchedule);
                        }}
                      />
                    </div>
                    <div className="w-32 flex flex-col">
                      <div className="text-xs mb-1 primary-text">百分比</div>
                      <div className="flex items-center gap-2">
                        <InputNumber
                          min={0}
                          max={100}
                          value={item.percentage}
                          onChange={(v) => {
                            const newSchedule = [...rolloutSchedule];
                            newSchedule[idx] = {
                              ...item,
                              percentage: Number(v) || 0,
                            };
                            setRolloutSchedule(newSchedule);
                          }}
                          style={{ width: '100%' }}
                          addonAfter="%"
                        />
                        <DeleteOutlined
                          onClick={() => {
                            setRolloutSchedule(rolloutSchedule.filter((_, i) => i !== idx));
                          }}
                          style={{
                            fontSize: '16px',
                            color: '#ff4d4f',
                            cursor: 'pointer',
                            flexShrink: 0,
                            padding: '4px',
                          }}
                          className="hover:opacity-70"
                        />
                      </div>
                    </div>
                  </div>
                ))}
                {rolloutSchedule.length === 0 || (rolloutSchedule.length > 0 && rolloutSchedule[rolloutSchedule.length - 1]?.percentage < 100) ? (
                  <Button
                    type="dashed"
                    onClick={() => {
                      const lastPercentage = rolloutSchedule.length > 0
                        ? rolloutSchedule[rolloutSchedule.length - 1].percentage
                        : 0;
                      const nextPercentage = lastPercentage >= 100
                        ? 100
                        : Math.min(lastPercentage + 10, 100);
                      setRolloutSchedule([
                        ...rolloutSchedule,
                        {
                          time: '',
                          percentage: nextPercentage,
                        },
                      ]);
                    }}
                    className="w-full"
                    disabled={rolloutSchedule.length > 0 && rolloutSchedule[rolloutSchedule.length - 1]?.percentage >= 100}
                  >
                    添加时间点
                  </Button>
                ) : (
                  <div className="text-xs mt-2 primary-text" style={{ color: 'var(--text-secondary)' }}>已达到100%，无需添加更多时间点</div>
                )}
              </div>
            )}

            {progressMode === 'manual' && (
              <div>
                <div className="text-sm mb-3 primary-text">灰度阶段配置</div>
                <div className="text-xs mb-2 primary-text" style={{ color: 'var(--text-secondary)' }}>配置多个阶段百分比，管理员在监控页面手动推进到下一阶段</div>
                {rolloutSchedule.map((item, idx) => (
                  <div key={idx} className="flex gap-2 p-3 mb-2 rounded-md border" style={{ borderColor: 'var(--border-primary)' }}>
                    <div className="flex items-center gap-3 flex-1">
                      <div className="w-6 h-6 rounded-full flex items-center justify-center text-sm self-start" style={{ marginTop: '24px', background: 'var(--color-primary-light)', color: 'var(--color-primary)' }}>{idx + 1}</div>
                      <div className="flex-1 flex flex-col">
                        <div className="text-xs mb-1 primary-text">阶段 {idx + 1}</div>
                        <div className="flex items-center gap-2">
                          <InputNumber
                            min={0}
                            max={100}
                            value={item.percentage}
                            onChange={(v) => {
                              const newSchedule = [...rolloutSchedule];
                              newSchedule[idx] = {
                                ...item,
                                percentage: Number(v) || 0,
                              };
                              setRolloutSchedule(newSchedule);
                            }}
                            style={{ width: '100%' }}
                            addonAfter="%"
                          />
                          <DeleteOutlined
                            onClick={() => {
                              setRolloutSchedule(rolloutSchedule.filter((_, i) => i !== idx));
                            }}
                            style={{
                              fontSize: '16px',
                              color: '#ff4d4f',
                              cursor: 'pointer',
                              flexShrink: 0,
                              padding: '4px',
                            }}
                            className="hover:opacity-70"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {rolloutSchedule.length === 0 || rolloutSchedule[rolloutSchedule.length - 1]?.percentage < 100 ? (
                  <Button
                    type="dashed"
                    onClick={() => {
                      const lastPercentage = rolloutSchedule.length > 0
                        ? rolloutSchedule[rolloutSchedule.length - 1].percentage
                        : 0;
                      const nextPercentage = Math.min(lastPercentage + 10, 100);
                      setRolloutSchedule([
                        ...rolloutSchedule,
                        {
                          time: '', // 手动模式不需要时间
                          percentage: nextPercentage,
                        },
                      ]);
                    }}
                    className="w-full"
                  >
                    添加阶段
                  </Button>
                ) : (
                  <div className="text-xs mt-2 primary-text" style={{ color: 'var(--text-secondary)' }}>已达到100%，无需添加更多阶段</div>
                )}
                <div className="text-xs mt-2 primary-text" style={{ color: 'var(--text-secondary)' }}>手动模式下，您需要在监控页面手动推进每个阶段</div>
              </div>
            )}
          </div>
        )}
      </Card>

      <div className="text-right">
        <Button type="primary" loading={saving} onClick={saveStrategy}>
          {hasStrategy ? '更新策略' : '配置策略'}
        </Button>
      </div>
    </div>
  );
};

const VersionManagement: React.FC = () => {
  const { tableRef, loading, setLoading, dataSource, setDataSource, pagination, updatePagination } = useTable<VersionWithModelName>();
  const { openDrawer } = useDrawer();
  const { config, showConfirmModal, hideConfirmModal, handleConfirm, handleCancel } = useConfirmModal();
  const { hasPermission } = usePermission();
  // 使用 ref 保存最新的 config，确保 handleConfirmAction 能访问到正确的值
  const configRef = useRef(config);
  useEffect(() => {
    configRef.current = config;
  }, [config]);

  // 筛选状态
  const [modelFilter, setModelFilter] = useState<number | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<VersionStatus | undefined>(undefined);
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [deviceModels, setDeviceModels] = useState<DeviceModel[]>([]);

  // 获取机型列表
  useEffect(() => {
    const fetchDeviceModels = async () => {
      try {
        const response = await deviceModelsAPI.getDeviceModels({ page: 1, size: 100 });
        setDeviceModels(response.list || []);
      } catch (error) {
        console.error('获取机型列表失败:', error);
      }
    };
    fetchDeviceModels();
  }, []);

  // 获取版本列表
  const fetchVersions = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.current,
        size: pagination.pageSize,
        ...(modelFilter && { model_id: modelFilter }),
        ...(statusFilter && { status: statusFilter }),
        ...(searchKeyword && { search: searchKeyword }),
      };

      const response = await versionsAPI.getVersions(params);

      console.log('API返回的版本列表数据:', response);

      // 将版本数据转换为包含机型名称的格式
      // 注意：后端返回的数据中已经包含 model_name，直接使用即可
      const versionsWithModelName: VersionWithModelName[] = (response.list || []).map((version: any) => {
        return {
          ...version,
          // 如果返回数据中已经有 model_name，直接使用；否则尝试从 deviceModels 中查找
          model_name: version.model_name || deviceModels.find((m) => m.id === version.model_id)?.name,
          key: String(version.id),
        };
      });

      console.log('处理后的版本列表数据:', versionsWithModelName);

      setDataSource(versionsWithModelName);
      updatePagination(pagination.current, pagination.pageSize, response.total);
    } catch (error: any) {
      console.error('获取版本列表失败:', error);
      message.error(error.message || '获取版本列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 初始化数据 - 当分页、筛选条件变化时重新获取
  useEffect(() => {
    fetchVersions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize, modelFilter, statusFilter, searchKeyword]);

  // 统计信息
  const totalVersions = pagination.total || 0;

  /**
   * 获取状态样式
   */
  const getStatusConfig = (status: VersionStatus) => {
    switch (status) {
      case 'publishing':
        return { color: 'success', text: '发布中' };
      case 'paused':
        return { color: 'warning', text: '已暂停' };
      case 'offline':
        return { color: 'default', text: '已下线' };
      case 'draft':
        return { color: 'default', text: '草稿' };
      case 'testing':
        return { color: 'processing', text: '测试中' };
      default:
        return { color: 'default', text: '未知' };
    }
  };

  /**
   * 列配置
   */
  const columns: TableColumn<VersionWithModelName>[] = [
    {
      title: '机型',
      dataIndex: 'model_name',
      key: 'model_name',
      width: 100,
      render: (text: string) => text || '未知',
    },
    {
      title: '版本号',
      dataIndex: 'version',
      key: 'version',
      width: 120,
      render: (v: string) => <span className="primary-text">{v}</span>,
    },
    // {
    //   title: '版本大小',
    //   dataIndex: 'package_size_mb',
    //   key: 'package_size_mb',
    //   width: 120,
    //   render: (size: number) => size ? `${size} MB` : '-',
    // },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: VersionStatus) => {
        const config = getStatusConfig(status);
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: '发布策略',
      dataIndex: 'strategy',
      key: 'strategy',
      width: 120,
      render: (_: string, record: VersionWithModelName) => (
        <Button
          type="link"
          onClick={() => handleConfigStrategy(record)}
          className="p-0 h-auto text-blue-500 hover:text-blue-400"
        >
          配置策略
        </Button>
      ),
    },
    {
      title: '差分包管理',
      key: 'diff_manage',
      width: 120,
      render: (_: any, record: VersionWithModelName) => (
        <Button
          type="link"
          onClick={() => handleManageDiff(record)}
          className="p-0 h-auto"
        >
          管理
        </Button>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (time: string) => time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
  ];

  /**
   * 添加版本
   */
  const handleAdd = () => {
    openDrawer({
      title: '创建新版本',
      content: (
        <VersionForm
          mode="add"
          onSuccess={() => {
            fetchVersions();
          }}
        />
      ),
      width: 800,
    });
  };

  /**
   * 编辑版本
   */
  const handleEdit = (record: VersionWithModelName) => {
    openDrawer({
      title: '编辑版本',
      content: (
        <VersionForm
          mode="edit"
          initialValues={record}
          onSuccess={() => {
            fetchVersions();
          }}
        />
      ),
      width: 800,
    });
  };

  /**
   * 配置策略
   */
  const handleConfigStrategy = (record: VersionWithModelName) => {
    openDrawer({
      title: '配置发布策略',
      content: (
        <StrategyForm
          version={record}
          onSuccess={() => {
            fetchVersions();
          }}
        />
      ),
      width: 720,
    });
  };

  /**
   * 差分包管理
   */
  const handleManageDiff = (record: VersionWithModelName) => {
    openDrawer({
      title: '差分包管理',
      content: (
        <DiffPackageManager version={record} />
      ),
      width: 720,
    });
  };

  /**
   * 确认操作
   */
  const handleConfirmAction = async () => {
    // 从 ref 中获取最新的 config，避免闭包问题
    const currentConfig = configRef.current;
    const { type, data } = currentConfig;

    console.log('handleConfirmAction 被调用，config:', { type, data, fullConfig: currentConfig });

    if (!data || !data.versionId) {
      console.error('确认操作失败：缺少版本ID', { type, data, fullConfig: currentConfig });
      message.error('操作失败：缺少版本ID');
      return;
    }

    try {
      switch (type) {
        case 'release':
          // 发布版本
          console.log('发布版本，版本ID:', data.versionId);
          await versionsAPI.publishVersion({ version_id: data.versionId });
          message.success(`版本 ${data.version || data.name} 发布成功！`);
          break;
        case 'pause':
          // 暂停发布
          console.log('暂停版本发布，版本ID:', data.versionId);
          await versionsAPI.pauseVersion({ version_id: data.versionId });
          message.success(`版本 ${data.version || data.name} 已暂停发布！`);
          break;
        case 'resume':
          // 恢复发布
          console.log('恢复版本发布，版本ID:', data.versionId);
          await versionsAPI.resumeVersion({ version_id: data.versionId });
          message.success(`版本 ${data.version || data.name} 已恢复发布！`);
          break;
        case 'delete':
          // 删除版本
          console.log('删除版本，版本ID:', data.versionId);
          await versionsAPI.deleteVersion(data.versionId);
          message.success(`版本 ${data.version || data.name} 删除成功！`);
          break;
        default:
          console.warn('未知的操作类型:', type);
          message.error('未知的操作类型');
          return;
      }
      fetchVersions();
    } catch (error: any) {
      console.error('操作失败:', error);
      message.error(error.message || '操作失败，请重试！');
    }
  };

  /**
   * 发布版本（显示确认弹窗）- 只有测试状态才能发布
   */
  const handlePublish = (record: VersionWithModelName) => {
    if (!record.id) {
      message.error('版本ID不存在');
      return;
    }

    showConfirmModal('release', {
      data: {
        version: record.version,
        name: record.version,
        versionId: record.id,
      },
      onConfirm: handleConfirmAction,
      onCancel: hideConfirmModal,
    });
  };

  /**
   * 配置测试设备（合并了变更测试状态的功能）
   * 如果是草稿状态，配置设备后会自动进入测试状态
   */
  const handleConfigTestDevices = (record: VersionWithModelName) => {
    openDrawer({
      title: record.status === 'draft' ? '进入测试' : '配置测试设备',
      content: (
        <TestDeviceForm
          version={record}
          onSuccess={() => {
            fetchVersions();
          }}
        />
      ),
      width: 720,
    });
  };

  /**
   * 暂停发布（显示确认弹窗）
   */
  const handlePause = (record: VersionWithModelName) => {
    if (!record.id) {
      message.error('版本ID不存在');
      return;
    }

    showConfirmModal('pause', {
      data: {
        version: record.version,
        name: record.version,
        versionId: record.id,
      },
      onConfirm: handleConfirmAction,
      onCancel: hideConfirmModal,
    });
  };


  /**
   * 删除版本
   */
  const handleDelete = (record: VersionWithModelName) => {
    if (!record.id) {
      message.error('版本ID不存在');
      return;
    }

    showConfirmModal('delete', {
      data: {
        version: record.version,
        name: record.version,
        versionId: record.id,
      },
      deleteConfig: {
        itemName: '版本',
        warningText: '删除后该版本将不再显示，关联的设备将无法获取此版本更新。',
        showWarning: true,
      },
      onConfirm: handleConfirmAction,
      onCancel: hideConfirmModal,
    });
  };

  /**
   * 操作列配置
   */
  const actions: TableAction<VersionWithModelName>[] = [
    {
      label: <EditOutlined />,
      key: 'edit',
      type: 'text',
      onClick: handleEdit,
      // 需要版本管理权限
      hidden: () => !hasPermission('version:manage'),
    },
    {
      label: <ExperimentOutlined />,
      key: 'config-test-devices',
      type: 'text',
      onClick: handleConfigTestDevices,
      // 需要策略配置权限，且草稿、测试中、已暂停状态下都可以配置测试设备
      hidden: (record: VersionWithModelName) => !hasPermission('strategy:config') || (record.status !== 'draft' && record.status !== 'testing' && record.status !== 'paused'),
    },
    {
      label: <PlayCircleOutlined />,
      key: 'publish',
      type: 'text',
      onClick: handlePublish,
      // 需要策略发布权限，且只有测试状态才显示发布按钮
      hidden: (record: VersionWithModelName) => !hasPermission('strategy:publish') || (record.status !== 'testing' && record.status !== 'paused'),
    },
    {
      label: <PauseCircleOutlined />,
      key: 'pause',
      type: 'text',
      onClick: handlePause,
      // 需要策略发布权限，且只有发布中状态才显示暂停按钮
      hidden: (record: VersionWithModelName) => !hasPermission('strategy:publish') || record.status !== 'publishing',
    },
    // {
    //   label: <ReloadOutlined />,
    //   key: 'resume',
    //   type: 'text',
    //   onClick: handleResume,
    //   // 只有暂停状态才显示恢复按钮
    //   hidden: (record: VersionWithModelName) => record.status !== 'paused',
    // },
    {
      label: <DeleteOutlined style={{ color: '#ff4d4f' }} />,
      key: 'delete',
      type: 'text',
      danger: true,
      onClick: handleDelete,
      // 需要版本管理权限
      hidden: () => !hasPermission('version:manage'),
    },
  ];

  /**
   * 处理搜索
   */
  const handleSearch = (value: string) => {
    setSearchKeyword(value);
    // 重置到第一页
    updatePagination(1, pagination.pageSize, pagination.total);
    fetchVersions();
  };


  return (
    <>
      <div className="space-y-6">
        <GlobalTable
          ref={tableRef}
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          header={{
            title: '版本列表',
            subtitle: `当前共 ${totalVersions} 个版本`,
            extra: (
              <PermissionWrapper permission="version:create">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  size="large"
                  onClick={handleAdd}
                  className="bg-accent-blue hover:bg-blue-600"
                >
                  创建新版本
                </Button>
              </PermissionWrapper>
            ),
          }}
          toolbar={{
            showSearch: true,
            searchPlaceholder: '搜索版本号...',
            onSearch: handleSearch,
            actions: [
              <Select
                key="model-filter"
                placeholder="全部机型"
                style={{ width: 140 }}
                size="large"
                value={modelFilter || 'all'}
                onChange={(value) => {
                  setModelFilter(value === 'all' ? undefined : (typeof value === 'number' ? value : undefined));
                  updatePagination(1, pagination.pageSize, pagination.total);
                }}
                options={[
                  { label: '全部机型', value: 'all' },
                  ...deviceModels.map((model) => ({
                    label: model.name,
                    value: model.id,
                  })),
                ]}
              />,
              <Select
                key="status-filter"
                placeholder="全部状态"
                style={{ width: 120 }}
                size="large"
                value={statusFilter || 'all'}
                onChange={(value) => {
                  setStatusFilter(value === 'all' ? undefined : value as VersionStatus);
                  updatePagination(1, pagination.pageSize, pagination.total);
                }}
                options={[
                  { label: '全部状态', value: 'all' },
                  { label: '草稿', value: 'draft' },
                  { label: '测试中', value: 'testing' },
                  { label: '发布中', value: 'publishing' },
                  { label: '已暂停', value: 'paused' },
                  { label: '已下线', value: 'offline' },
                ]}
              />,
            ],
          }}
          actions={actions}
          actionsWidth={120}
          pagination={{
            ...pagination,
            total: totalVersions,
            showTotal: true,
            onChange: (page, pageSize) => {
              updatePagination(page, pageSize, totalVersions);
            },
          }}
          scroll={{ x: 1000 }}
        />
      </div>

      {/* 全局确认框 */}
      <ConfirmModal
        config={{
          ...config,
          onConfirm: handleConfirm,
          onCancel: handleCancel,
        }}
      />


    </>
  )
};

export default VersionManagement;


